import type {
  EarningStatus,
  Panelist,
  PointsBalance,
  PublicPanel,
  Task,
} from "./types";
import { formatGbp } from "./utils/currency";

export function initials(first: string, last: string) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "--";
}

export function applyBalanceToPanelist(
  panelist: Panelist | undefined,
  balance: PointsBalance | undefined,
  panel: PublicPanel | undefined,
) {
  if (!panelist || !balance) return panelist;

  const points = balance.availableBalancePts;

  return {
    ...panelist,
    points,
    pointsGbp:
      panel?.pointsPerGBP && panel.pointsPerGBP > 0
        ? formatGbp(points / panel.pointsPerGBP)
        : panelist.pointsGbp,
  };
}

export function walletState(panel: PublicPanel | undefined, points: number) {
  if (!panel) return { minimum: 0, canCashout: false, pct: 0, toNext: 0 };
  const valuePoints = points;
  const valueGbp = valuePoints / (panel.pointsPerGBP || 0);
  const minimumGbp = panel.minCashoutValue ?? 10;
  const minimumPoints = minimumGbp * (panel.pointsPerGBP || 0);
  const minimum = minimumPoints;
  const canCashout = valuePoints >= minimumPoints;
  const pct = Math.min(100, Math.round((valuePoints / minimumPoints) * 100));
  const toNext = Math.max(0, Math.round(minimumPoints - valuePoints));

  return { minimum, canCashout, pct, toNext };
}

export function taskTopic(task: Task) {
  return task.metadata?.topic || task.category || "";
}

export function taskCategory(task: Task) {
  return task.category || task.metadata?.topic || "";
}

export function estimatedMinutes(task: Task) {
  return task.metadata?.estimatedMins ?? "?";
}

export function taskTagLabel(tag: string) {
  if (tag === "new") return "New";
  if (tag === "popular") return "Popular";
  return "";
}

export function taskTagClass(tag: string) {
  if (tag === "new") return "tag-green";
  if (tag === "popular") return "tag-amber";
  return "tag-grey";
}

export function statusLabel(status: EarningStatus) {
  const labels: Record<EarningStatus, string> = {
    paid: "Paid",
    pending: "Pending",
    failed: "No match",
    qa_fail: "Quality fail",
    accepted: "Accepted",
    blacklist: "Blacklisted",
    error: "Error",
    timeout: "Timeout",
    completed: "Completed",
  };

  return labels[status];
}

export function statusTagClass(status: EarningStatus) {
  const classes: Record<EarningStatus, string> = {
    paid: "tag-green",
    pending: "tag-amber",
    failed: "tag-grey",
    qa_fail: "tag-red",
    accepted: "tag-green",
    blacklist: "tag-red",
    error: "tag-red",
    timeout: "tag-red",
    completed: "tag-amber",
  };

  return classes[status];
}

export function pageTitle(pathname: string) {
  if (pathname === "/") return "";
  if (pathname === "/dashboard") return "Dashboard";
  if (pathname === "/tasks") return "Available tasks";
  if (pathname.startsWith("/tasks/")) return "Task detail";
  if (pathname === "/completion") return "Survey complete";
  if (pathname === "/screenout") return "Not this time";
  if (pathname === "/earnings") return "Earnings history";
  if (pathname === "/profile/edit") return "Edit profile";
  if (pathname === "/profile") return "My profile";

  return "";
}

export function activeNavKey(pathname: string) {
  if (pathname === "/") return "/";
  if (pathname === "/dashboard") return "dashboard";
  if (
    pathname === "/tasks" ||
    pathname.startsWith("/tasks/") ||
    pathname === "/completion" ||
    pathname === "/screenout"
  ) {
    return "tasks";
  }
  if (pathname === "/earnings" || pathname === "/cashout") return "earnings";
  if (pathname === "/profile" || pathname === "/profile/edit") return "profile";

  return "";
}
export function bigFigure(value: number): string {
  const units = [
    { threshold: 1_000_000_000, suffix: "b" },
    { threshold: 1_000_000, suffix: "m" },
    { threshold: 1_000, suffix: "k" },
  ];

  const unit = units.find(({ threshold }) => Math.abs(value) >= threshold);

  if (!unit) {
    return value.toLocaleString();
  }

  const compactValue = value / unit.threshold;

  return `${Number(compactValue.toFixed(2))}${unit.suffix}`;
}
const integerFormatter = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 0,
});

export function formatNumber(value: number): string {
  if (value >= 1_000_000) return bigFigure(value);
  return integerFormatter.format(value);
}
